import { useAuth0 } from "@auth0/auth0-react";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import { Badge, Box, Button, IconButton, Tooltip, Typography } from "@mui/material";
import PropTypes from "prop-types";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Link, useLocation } from "react-router";

import { DriveManagementMenu, Menu } from "@/atoms";
import { fileData, identifiersData, inputData, namesData, permissionsData, urlsData } from "@/data";
import { useUploadFile } from "@/hooks";
import { closeFilters, openFilters } from "@/redux/actions";
import { StyledPageHead, StyledPageHeadTitle } from "@/styles";
import { theme } from "@/theme";
import { canDo } from "@/utils";

const PageHead = ({
    actionComponent,
    addAction,
    addRouteUrl,
    backAction,
    backRouteUrl,
    createNewFolderAction,
    enableUploadFiles,
    exportAction,
    filtersCount,
    filtersModule,
    hasAction,
    hasAdd,
    hasBack,
    hasBulkUpdate,
    hasDriveManagementMenu,
    hasExport,
    hasFilters,
    hasSelect,
    hasSubtitle,
    isDrivePageHead,
    selectAction,
    selected = [],
    subtitle,
    title,
    titleTag,
    uploadFilesAction,
}) => {
    const { t } = useTranslation();

    const filtersOpened = useSelector((state) => state.filtersReducer.open);

    const dispatch = useDispatch();

    const location = useLocation();

    const { user } = useAuth0();

    const { filesLoading, uploadFile } = useUploadFile();

    const directoryRef = useRef(null);

    const { accountCreate: accountCreatePermission, mediaCreate: mediaCreatePermission } =
        permissionsData.permissions.keys;

    const allowedPermissions =
        location.pathname === urlsData.routes.users.url ? [accountCreatePermission] : [mediaCreatePermission];

    const { button: buttonId } = identifiersData.ids;

    const {
        back: backButtonName,
        bulkUpdate: bulkUpdateButtonName,
        closeFilters: closeFiltersButtonName,
        create: createButtonName,
        openFilters: openFiltersButtonName,
        select: selectButtonName,
    } = namesData.buttons;

    const { file: fileInputName } = inputData.names;

    const { input: inputId } = identifiersData.ids;

    return (
        <StyledPageHead>
            <StyledPageHeadTitle>
                {hasBack && (
                    <Link to={backRouteUrl} onClick={backAction}>
                        <Tooltip title={t("actions.back")}>
                            <IconButton id={`${backButtonName}_${buttonId}`}>
                                <ArrowBackOutlinedIcon
                                    sx={{
                                        color: theme.palette.grey[700],
                                        fontSize: 25,
                                    }}
                                />
                            </IconButton>
                        </Tooltip>
                    </Link>
                )}
                {hasDriveManagementMenu ? (
                    <DriveManagementMenu
                        createNewFolderAction={createNewFolderAction}
                        enableUploadFiles={enableUploadFiles}
                        uploadFilesAction={uploadFilesAction}
                    />
                ) : (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: hasSubtitle ? 0.5 : 0,
                        }}
                    >
                        <Typography component={titleTag || "h1"} variant="h6">
                            {title}
                        </Typography>
                        {hasSubtitle && (
                            <Typography color={theme.palette.grey[600]} variant="body2">
                                {subtitle}
                            </Typography>
                        )}
                    </Box>
                )}
            </StyledPageHeadTitle>
            <Box
                sx={{
                    display: "flex",
                    gap: hasAdd && hasFilters && 2,
                }}
            >
                {hasExport && selected.length > 0 && (
                    <Box
                        sx={{
                            mt: 1,
                            mx: 1,
                        }}
                    >
                        <Menu
                            label={t("actions.exportData")}
                            name={namesData.menus.exportData}
                            options={[
                                {
                                    action: () => exportAction(selected),
                                    label: t("labels.csv"),
                                },
                            ]}
                            withTransparentButton
                        />
                    </Box>
                )}
                {hasSelect && (
                    <Box
                        sx={{
                            mt: 1,
                            mx: 1,
                        }}
                    >
                        <Button id={`${selectButtonName}_${buttonId}`} onClick={() => selectAction(true)}>
                            {t("actions.select")}
                        </Button>
                    </Box>
                )}
                {hasBulkUpdate && (
                    <Button
                        disabled={!filesLoading}
                        id={`${bulkUpdateButtonName}_${buttonId}`}
                        startIcon={<EditOutlinedIcon />}
                        sx={{
                            bgColor: theme.palette.primary.main,
                        }}
                        onClick={() => {
                            document.getElementById(`${fileInputName}_${inputId}`).value = "";

                            document.getElementById(`${fileInputName}_${inputId}`).click();
                        }}
                    >
                        <Typography
                            variant="caption"
                            sx={{
                                fontWeight: "bold",
                            }}
                        >
                            {t("actions.update")}
                        </Typography>
                        <input
                            accept={fileData.acceptedExtensions.zip}
                            id={`${fileInputName}_${inputId}`}
                            ref={directoryRef}
                            style={{ display: "none" }}
                            type={inputData.types.file}
                            onChange={(e) =>
                                uploadFile(
                                    e.target.files[0],
                                    null,
                                    location.pathname.includes(urlsData.routes.releases.url)
                                        ? fileData.names.bulkUpdateRelease
                                        : fileData.names.bulkUpdateTrack,
                                )
                            }
                        />
                    </Button>
                )}
                {hasAdd && (canDo(user, allowedPermissions) || isDrivePageHead) && (
                    <Box>
                        {addRouteUrl ? (
                            <Link to={addRouteUrl}>
                                <Tooltip title={t("actions.create")}>
                                    <IconButton id={`${createButtonName}_${buttonId}`}>
                                        <AddCircleIcon
                                            sx={{
                                                color: theme.palette.primary.main,
                                                fontSize: 30,
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Link>
                        ) : (
                            <Tooltip title={t("actions.create")}>
                                <IconButton id={`${createButtonName}_${buttonId}`} onClick={addAction}>
                                    <AddCircleIcon
                                        sx={{
                                            color: theme.palette.primary.main,
                                            fontSize: 30,
                                        }}
                                    />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>
                )}
                {hasAction && (
                    <Box
                        sx={{
                            alignItems: "center",
                            display: "flex",
                            justifyContent: "center",
                        }}
                    >
                        {actionComponent}
                    </Box>
                )}
                {hasFilters && (
                    <Tooltip title={filtersOpened ? t("actions.closeFilters") : t("actions.openFilters")}>
                        <IconButton
                            id={`${filtersOpened ? closeFiltersButtonName : openFiltersButtonName}_${buttonId}`}
                            onClick={() =>
                                filtersOpened ? dispatch(closeFilters()) : dispatch(openFilters(filtersModule))
                            }
                        >
                            <Badge badgeContent={filtersCount} color="error" variant="solid">
                                <FilterListIcon
                                    sx={{
                                        background: filtersOpened
                                            ? theme.palette.primary.main
                                            : theme.palette.grey[100],
                                        borderRadius: "50%",
                                        color: filtersOpened ? theme.palette.grey[100] : theme.palette.primary.main,
                                        fontSize: 25,
                                        padding: filtersOpened && 0.5,
                                    }}
                                />
                            </Badge>
                        </IconButton>
                    </Tooltip>
                )}
            </Box>
        </StyledPageHead>
    );
};

export default PageHead;

PageHead.propTypes = {
    actionComponent: PropTypes.node,
    addAction: PropTypes.func,
    addRouteUrl: PropTypes.string,
    backAction: PropTypes.func,
    backRouteUrl: PropTypes.string,
    createNewFolderAction: PropTypes.func,
    enableUploadFiles: PropTypes.bool,
    exportAction: PropTypes.func,
    filtersCount: PropTypes.number,
    filtersModule: PropTypes.string,
    hasAction: PropTypes.bool,
    hasAdd: PropTypes.bool,
    hasBack: PropTypes.bool,
    hasBulkUpdate: PropTypes.bool,
    hasDriveManagementMenu: PropTypes.bool,
    hasExport: PropTypes.bool,
    hasFilters: PropTypes.bool,
    hasSelect: PropTypes.bool,
    hasSubtitle: PropTypes.bool,
    isDrivePageHead: PropTypes.bool,
    selectAction: PropTypes.func,
    selected: PropTypes.array,
    subtitle: PropTypes.string,
    title: PropTypes.string,
    titleTag: PropTypes.string,
    uploadFilesAction: PropTypes.func,
};
